
import React from "react";
import { Loader2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import NameItem from "./NameItem";

interface NamesListProps {
  title: string;
  items: Array<{id: string, name: string, category?: string}>;
  isLoading: boolean;
  onAdd: (name: string, category?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onEdit?: (id: string, newName: string, newCategory: string) => Promise<void>;
  nameField?: string;
  placeholder?: string;
}

const NamesList: React.FC<NamesListProps> = ({ 
  title, 
  items, 
  isLoading, 
  onAdd,
  onDelete,
  onEdit,
  nameField = "name",
  placeholder = "Add new item..."
}) => {
  const [newItem, setNewItem] = React.useState("");
  const [newCategory, setNewCategory] = React.useState("");
  const [isAdding, setIsAdding] = React.useState(false);

  const handleAdd = async () => {
    if (newItem.trim()) {
      setIsAdding(true);
      try {
        await onAdd(newItem.trim(), newCategory.trim() || undefined);
        setNewItem("");
        setNewCategory("");
      } finally {
        setIsAdding(false);
      }
    }
  };

  return (
    <div className="border rounded-md p-4 bg-gray-50">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-4">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder={placeholder}
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Category (optional)"
              className="w-1/3"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <Button 
              onClick={handleAdd} 
              disabled={isAdding || !newItem.trim()} 
              className="bg-elfGreen hover:bg-elfGreen-dark"
            >
              {isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>

          <div className="max-h-60 overflow-y-auto space-y-2">
            {items.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No items yet</div>
            ) : (
              items.map((item) => (
                <NameItem
                  key={item.id}
                  id={item.id}
                  name={item.name}
                  category={item.category}
                  onDelete={onDelete}
                  onEdit={onEdit}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NamesList;
